import * as Sentry from "@sentry/browser";
import { message } from "antd";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import React, { useCallback, useState } from "react";
import { GLOBAL_SEARCH_COMPANY } from "../../gql/companies";
import {
  CUSTOMER_INTERACTIONS_GLOBAL_SEARCH_QUERY,
  GLOBAL_SEARCH_CUSTOMER,
} from "../../gql/customers";
import { GLOBAL_SEARCH_ONLINE_TRANSACTIONS } from "../../gql/online-transactions";
import { GLOBAL_SEARCH_REDEMPTION_REQUEST } from "../../gql/redemption-request";
import { GLOBAL_SEARCH_TRANSFER_REQUEST } from "../../gql/transfer-requests";
import {
  Activity,
  Company,
  Customer,
  CustomerTransaction,
  Loading,
  RedemptionsRequest,
  SearchPayload,
  TransferRequest,
} from "../../helpers/shared-interfaces";
import { request, responseHandler } from "../../helpers/utilities";
import "../../styles/components/GlobalForm.css";
import { ItemClickProps } from "./GlobalFormModal";
import SearchResults from "./SearchResults";
import { DEBOUNCE_TIME } from "../../helpers/constants";


interface SearchResult {
  id: string;
  name: string;
  date: string;
  email: string;
  phone: string;
  category: string;
}

export enum SearchTag {
  CUSTOMERS = "Customers",
  REDEMPTIONS = "Redemptions",
  TRANSFERS = "Transfers",
  CLIENTS = "Clients",
  ONLINE_TRANSACTIONS = "Online Transactions",
  CUSTOMER_INTERACTIONS = "Customer Interactions",
}

export interface State {
  activeTag: SearchTag;
  loading: Loading;
  searchData: SearchResult[];
  searchTerm: string;
  customers: Customer[];
  companies: Company[];
  redemptions: RedemptionsRequest[];
  transfers: TransferRequest[];
  onlineTransactions: CustomerTransaction[];
  customerInteractions: Activity[];
}

type Props = {
  handleItemClick: (item: ItemClickProps) => void;
};

interface SearchConfig {
  query: string;
  stateKey: keyof Pick<
    State,
    | "customers"
    | "companies"
    | "redemptions"
    | "transfers"
    | "onlineTransactions"
    | "customerInteractions"
  >;
  gqlQuery: string;
  responseKey: string;
  errorMessage: string;
}

const SEARCH_CONFIGS: Record<SearchTag, SearchConfig> = {
  [SearchTag.CUSTOMERS]: {
    stateKey: "customers",
    gqlQuery: GLOBAL_SEARCH_CUSTOMER,
    responseKey: "customerSearch",
    errorMessage: "customer search failed",
    query: "",
  },
  [SearchTag.CLIENTS]: {
    stateKey: "companies",
    gqlQuery: GLOBAL_SEARCH_COMPANY,
    responseKey: "companySearch",
    errorMessage: "client search failed",
    query: "",
  },
  [SearchTag.REDEMPTIONS]: {
    stateKey: "redemptions",
    gqlQuery: GLOBAL_SEARCH_REDEMPTION_REQUEST,
    responseKey: "redemptionRequestSearch",
    errorMessage: "redemption search failed",
    query: "",
  },
  [SearchTag.TRANSFERS]: {
    stateKey: "transfers",
    gqlQuery: GLOBAL_SEARCH_TRANSFER_REQUEST,
    responseKey: "transferRequestSearch",
    errorMessage: "transfer search failed",
    query: "",
  },
  [SearchTag.ONLINE_TRANSACTIONS]: {
    stateKey: "onlineTransactions",
    gqlQuery: GLOBAL_SEARCH_ONLINE_TRANSACTIONS,
    responseKey: "onlineTransactions",
    errorMessage: "online transaction search failed",
    query: "",
  },
  [SearchTag.CUSTOMER_INTERACTIONS]: {
    stateKey: "customerInteractions",
    gqlQuery: CUSTOMER_INTERACTIONS_GLOBAL_SEARCH_QUERY,
    responseKey: "customerInteractionSearch",
    errorMessage: "customer interaction search failed",
    query: "",
  },
};

const GlobalForm: React.FC<Props> = ({ handleItemClick }): JSX.Element => {
  const [state, setState] = useState<State>({
    activeTag: SearchTag.CUSTOMERS,
    loading: Loading.INITIAL,
    searchData: [],
    searchTerm: "",
    customers: [],
    companies: [],
    redemptions: [],
    transfers: [],
    onlineTransactions: [],
    customerInteractions: [],
  });

  const handleSearch = async <T,>({
    query,
    stateKey,
    gqlQuery,
    responseKey,
    errorMessage,
  }: SearchConfig) => {
    setState((prevState) => ({
      ...prevState,
      loading: Loading.LOADING,
    }));

    try {
      const r: AxiosResponse = await request({
        query: gqlQuery,
        variables: { q: query, page: 1 },
      });

      const { err, mutation } = responseHandler<SearchPayload<T>>(
        r,
        responseKey,
      );

      if (err) {
        message.error(errorMessage);
        setState((prevState) => ({
          ...prevState,
          loading: Loading.ERROR,
        }));
      } else {
        setState((prevState) => ({
          ...prevState,
          [stateKey]: mutation.results,
          loading: Loading.SUCCESS,
        }));
      }
    } catch (error) {
      message.error(errorMessage);
      Sentry.captureException(error);
      setState((prevState) => ({
        ...prevState,
        loading: Loading.ERROR,
      }));
    }
  };

  const handleTagSearch = (query: string, tag: SearchTag) => {
    const config = SEARCH_CONFIGS[tag];
    return handleSearch({
      ...config,
      query,
    });
  };

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      if (query.trim().length >= 3) {
        handleTagSearch(query, state.activeTag);
      }
    }, DEBOUNCE_TIME),
    [state.activeTag],
  );

  const handleTagClick = (tag: SearchTag) => {
    setState((prevState) => ({
      ...prevState,
      activeTag: tag,
    }));

    if (state.searchTerm.trim().length >= 3) {
      handleTagSearch(state.searchTerm, tag);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target?.value ?? "";

    setState((prevState) => ({
      ...prevState,
      searchTerm: newValue,
    }));
    debouncedSearch(newValue);
  };

  return (
    <div className="global-form">
      <input
        type="text"
        className="global-search-input"
        placeholder="Search for anything..."
        autoFocus
        value={state.searchTerm}
        onChange={handleInputChange}
      />
      <div className="global-tags">
        {[
          SearchTag.CUSTOMERS,
          SearchTag.CLIENTS,
          SearchTag.REDEMPTIONS,
          SearchTag.TRANSFERS,
          SearchTag.ONLINE_TRANSACTIONS,
          SearchTag.CUSTOMER_INTERACTIONS,
        ].map((tag) => (
          <span
            key={tag}
            className={`global-tag ${state.activeTag === tag ? "active" : ""}`}
            onClick={() => handleTagClick(tag)}
          >
            {tag}
          </span>
        ))}
      </div>
      <SearchResults
        activeTag={state.activeTag}
        state={state}
        handleItemClick={handleItemClick}
        handleRetry={(query: string) => handleTagSearch(query, state.activeTag)}
      />
    </div>
  );
};

export default GlobalForm;
